'use client';

// Global map để track selected RPCs
const selectedRpcs = new Map<number, string>();

// Get selected RPC for a chain
export function getSelectedRpc(chainId: number): string | null {
  // Check memory first
  const cached = selectedRpcs.get(chainId);
  if (cached) return cached;
  
  // Check localStorage
  const saved = localStorage.getItem(`helio-selected-rpc-${chainId}`);
  if (saved) {
    selectedRpcs.set(chainId, saved);
    return saved;
  }
  
  return null;
}

// Set selected RPC for a chain
export function setSelectedRpc(chainId: number, rpc: string) {
  selectedRpcs.set(chainId, rpc);
  localStorage.setItem(`helio-selected-rpc-${chainId}`, rpc);
}

// Override fetch to intercept RPC calls
export function setupWagmiRpcOverride() {
  if (typeof window === 'undefined') return;
  
  const originalFetch = window.fetch;
  
  // Track current chain ID from responses
  let currentChainId: number | null = null;
  
  window.fetch = async function(...args: any[]) {
    let [resource, options] = args;
    let url: string | undefined;
    
    // Extract URL
    if (typeof resource === 'string') {
      url = resource;
    } else if (resource instanceof Request) {
      url = resource.url;
    } else if (resource instanceof URL) {
      url = resource.toString();
    }
    
    // Check if this is an RPC call
    if (url && options?.body) {
      try {
        const body = typeof options.body === 'string' 
          ? JSON.parse(options.body) 
          : options.body;
          
        // Check if it's an Ethereum JSON-RPC call
        if (body && (body.method || (Array.isArray(body) && body[0]?.method))) {
          // Use current chain ID if available
          let chainId = currentChainId;
          
          // Method 1: Direct chain detection from URL
          if (!chainId) {
            if (url.includes('56.rpc') || url.includes('bsc')) chainId = 56;
            else if (url.includes('1.rpc') || url.includes('ethereum') || url.includes('mainnet')) chainId = 1;
            else if (url.includes('137.rpc') || url.includes('polygon')) chainId = 137;
            else if (url.includes('43114') || url.includes('avalanche')) chainId = 43114;
            else if (url.includes('42161') || url.includes('arbitrum')) chainId = 42161;
          }
          
          // If we have a chain and selected RPC, redirect
          if (chainId) {
            const selectedRpc = getSelectedRpc(chainId);
            if (selectedRpc && url !== selectedRpc && !url.includes('localhost')) {
              // console.log(`[Wagmi Override] Redirecting chain ${chainId} RPC from ${url} to ${selectedRpc}`);
              
              // Update args for original fetch
              if (typeof resource === 'string') {
                args[0] = selectedRpc;
              } else if (resource instanceof Request) {
                args[0] = new Request(selectedRpc, resource);
              }
            }
          }
          
          // Make the request and track chain ID from response
          const response = await originalFetch.apply(this, args as [URL | RequestInfo, RequestInit?]);
          const clonedResponse = response.clone();
          
          try {
            const data = await clonedResponse.json();
            
            // Track chain ID from eth_chainId responses
            if (body.method === 'eth_chainId' && data.result) {
              currentChainId = parseInt(data.result, 16);
              // console.log(`[Wagmi Override] Detected chain ID: ${currentChainId}`);
            } else if (Array.isArray(body)) {
              const chainIdCall = body.find(call => call.method === 'eth_chainId');
              if (chainIdCall && Array.isArray(data)) {
                const result = data.find(r => r.id === chainIdCall.id);
                if (result?.result) {
                  currentChainId = parseInt(result.result, 16);
                  // console.log(`[Wagmi Override] Detected chain ID from batch: ${currentChainId}`);
                }
              }
            }
          } catch (e) {
            // Ignore parse errors
          }
          
          return response;
        }
      } catch (e) {
        // Ignore parse errors
      }
    }
    
    // Call original fetch
    return originalFetch.apply(this, args as [URL | RequestInfo, RequestInit?]);
  };
  
  // console.log('[Wagmi Override] RPC override initialized');
} 